import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Button,
  Dropdown,
  Empty,
  Input,
  Menu,
  Modal,
  Spin,
  Tooltip,
  message as antdMessage,
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  CheckCircleOutlined,
  DownOutlined,
  ExclamationCircleOutlined,
  LoadingOutlined,
  MoreOutlined,
  PlusOutlined,
  RobotOutlined,
  SendOutlined,
  StopOutlined,
  UpOutlined,
} from "@ant-design/icons";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import {
  deleteFacilityAiConversation,
  getFacilityAiConversationMessages,
  listFacilityAiConversations,
  streamFacilityAiChat,
  updateFacilityAiConversation,
} from "./api";
import "./styles.css";

const { TextArea } = Input;

const TOOL_LABELS = {
  search_faq: "searching product knowledge",
  query_database: "querying authorized facility data",
  calculate_savings: "calculating savings data",
  get_current_time: "checking business time",
  controlled_fallback: "using a controlled data query",
};

const PROGRESS_EVENTS = new Set([
  "start",
  "plan",
  "tool_start",
  "tool_result",
  "reflection",
  "result_validated",
  "answer_delta",
  "answer_done",
  "error",
  "end",
]);

const progressToolLabel = (tool) => TOOL_LABELS[tool] || tool || "an AI tool";

const progressEventCopy = (payload) => {
  const event = payload?.event || "progress";
  const toolLabel = progressToolLabel(payload?.tool);
  const count = Number.isFinite(Number(payload?.count)) ? Number(payload.count) : null;

  switch (event) {
    case "start":
      return { title: "Starting request", detail: "Connecting to the AI service and preparing the facility scope" };
    case "plan":
      return { title: "Planning steps", detail: payload?.summary || "Selecting the needed data sources" };
    case "tool_start":
      return { title: `Running ${toolLabel}`, detail: "The tool is retrieving authorized information" };
    case "tool_result":
      return {
        title: `${toolLabel} completed`,
        detail: count === null ? "Tool result received; validating it" : `${count} evidence item(s) received; validating them`,
      };
    case "reflection":
      if (
        payload?.status === "answer_synthesized" ||
        payload?.status === "answer_synthesis_started"
      ) {
        return { title: "Preparing final answer", detail: "Verified evidence is being formatted for the response" };
      }
      if (payload?.status === "limit_reached") {
        return { title: "Evidence limit reached", detail: "The response will use the authorized evidence collected so far" };
      }
      return { title: "Validating evidence", detail: "Checking whether the evidence supports the requested answer" };
    case "result_validated":
      return {
        title: "Evidence validated",
        detail: count === null ? "Facility scope validation completed" : `${count} evidence item(s) validated`,
      };
    case "answer_delta":
      return { title: "Generating answer", detail: "Answer content is arriving in real time" };
    case "answer_done":
      return { title: "Answer generated", detail: "Finalizing the message" };
    case "error":
      return { title: "Request needs attention", detail: payload?.detail || payload?.message || "The AI service returned a controlled error" };
    case "end":
      return { title: "Request completed", detail: "This AI request has ended" };
    default:
      return { title: "AI is working", detail: "Processing the request" };
  }
};

const createProgressEvent = (payload) => {
  const copy = progressEventCopy(payload);
  return {
    id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
    event: payload?.event || "progress",
    ...copy,
  };
};

const createInitialProgress = () => ({
  status: "working",
  expanded: true,
  summary: "AI is working",
  events: [
    {
      id: `queued-${Date.now()}`,
      event: "queued",
      title: "Request sent",
      detail: "Waiting for the first AI status update",
    },
  ],
});

const getCollectionItems = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload?.data?.results)) return payload.data.results;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

const normalizeMessage = (item) => ({
  ...item,
  content_markdown: item?.content_markdown || "",
});

const formatConversationDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
  }).format(date);
};

const FacilityAiAssistant = () => {
  const { companyId, facilityId } = useParams();
  const [conversations, setConversations] = useState([]);
  const [selectedConversationId, setSelectedConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [conversationsLoading, setConversationsLoading] = useState(false);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [renameConversation, setRenameConversation] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [renameSaving, setRenameSaving] = useState(false);
  const messagesEndRef = useRef(null);
  const streamControllerRef = useRef(null);
  const scopeRef = useRef("");

  const selectedConversation = useMemo(
    () => conversations.find((conversation) => conversation.id === selectedConversationId) || null,
    [conversations, selectedConversationId]
  );

  const upsertConversation = useCallback((conversation) => {
    if (!conversation?.id) return;

    setConversations((current) => {
      const next = [conversation, ...current.filter((item) => item.id !== conversation.id)];
      return next.sort((left, right) => {
        const leftTime = new Date(left.last_message_at || left.updated_at || left.created_at || 0).getTime();
        const rightTime = new Date(right.last_message_at || right.updated_at || right.created_at || 0).getTime();
        return rightTime - leftTime;
      });
    });
  }, []);

  const loadConversations = useCallback(async () => {
    if (!companyId || !facilityId) return;

    setConversationsLoading(true);
    try {
      const payload = await listFacilityAiConversations(companyId, facilityId);
      setConversations(getCollectionItems(payload));
    } catch (error) {
      antdMessage.error(error?.message || "Unable to load AI conversations.");
    } finally {
      setConversationsLoading(false);
    }
  }, [companyId, facilityId]);

  const loadMessages = useCallback(
    async (conversationId) => {
      if (!conversationId || !companyId || !facilityId) return;

      setMessagesLoading(true);
      try {
        const payload = await getFacilityAiConversationMessages(companyId, facilityId, conversationId);
        setMessages(getCollectionItems(payload).map(normalizeMessage));
      } catch (error) {
        antdMessage.error(error?.message || "Unable to load this conversation.");
      } finally {
        setMessagesLoading(false);
      }
    },
    [companyId, facilityId]
  );

  useEffect(() => {
    const scopeKey = `${companyId || ""}:${facilityId || ""}`;
    scopeRef.current = scopeKey;
    streamControllerRef.current?.abort();
    setSelectedConversationId(null);
    setMessages([]);
    loadConversations();

    return () => {
      streamControllerRef.current?.abort();
    };
  }, [companyId, facilityId, loadConversations]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages, isStreaming]);

  useEffect(() => {
    return () => {
      streamControllerRef.current?.abort();
    };
  }, []);

  const handleSelectConversation = async (conversationId) => {
    if (isStreaming || conversationId === selectedConversationId) return;

    setSelectedConversationId(conversationId);
    setMessages([]);
    await loadMessages(conversationId);
  };

  const handleNewConversation = () => {
    if (isStreaming) return;
    setSelectedConversationId(null);
    setMessages([]);
    setDraft("");
  };

  const handleRename = async () => {
    const title = renameValue.trim();
    if (!renameConversation || !title) {
      antdMessage.error("Enter a conversation name.");
      return;
    }

    setRenameSaving(true);
    try {
      const updated = await updateFacilityAiConversation(
        companyId,
        facilityId,
        renameConversation.id,
        { title }
      );
      upsertConversation(updated);
      setRenameConversation(null);
    } catch (error) {
      antdMessage.error(error?.message || "Unable to rename the conversation.");
    } finally {
      setRenameSaving(false);
    }
  };

  const handleDelete = (conversation) => {
    Modal.confirm({
      title: "Delete conversation?",
      content: "This removes the conversation and its AI context for this facility.",
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await deleteFacilityAiConversation(companyId, facilityId, conversation.id);
          setConversations((current) => current.filter((item) => item.id !== conversation.id));
          if (selectedConversationId === conversation.id) {
            setSelectedConversationId(null);
            setMessages([]);
          }
        } catch (error) {
          antdMessage.error(error?.message || "Unable to delete the conversation.");
          throw error;
        }
      },
    });
  };

  const updateStreamingMessage = useCallback((localMessageId, patch) => {
    setMessages((current) =>
      current.map((item) => (item.id === localMessageId ? { ...item, ...patch } : item))
    );
  }, []);

  const appendProgressEvent = useCallback((localMessageId, eventPayload) => {
    if (!PROGRESS_EVENTS.has(eventPayload?.event)) return;

    const progressEvent = createProgressEvent(eventPayload);
    setMessages((current) =>
      current.map((item) => {
        if (item.id !== localMessageId) return item;
        const progress = item.progress || createInitialProgress();
        const status = eventPayload.event === "error" ? "error" : progress.status;
        return {
          ...item,
          progress: {
            ...progress,
            status,
            summary: progressEvent.title,
            events: [...progress.events, progressEvent].slice(-24),
          },
        };
      })
    );
  }, []);

  const updateProgressStatus = useCallback((localMessageId, status, summary, expanded) => {
    setMessages((current) =>
      current.map((item) => {
        if (item.id !== localMessageId) return item;
        const progress = item.progress || createInitialProgress();
        return {
          ...item,
          progress: {
            ...progress,
            status,
            summary,
            ...(typeof expanded === "boolean" ? { expanded } : {}),
          },
        };
      })
    );
  }, []);

  const appendAnswerDelta = useCallback((localMessageId, delta) => {
    setMessages((current) =>
      current.map((item) => {
        if (item.id !== localMessageId) return item;
        const progress = item.progress || createInitialProgress();
        const hasAnswerProgress = progress.events.some(
          (itemEvent) => itemEvent.event === "answer_delta"
        );
        const answerProgressEvent = hasAnswerProgress
          ? null
          : createProgressEvent({ event: "answer_delta" });
        return {
          ...item,
          content_markdown: `${item.content_markdown}${delta || ""}`,
          progress: {
            ...progress,
            status: "working",
            summary: "Generating answer",
            events: answerProgressEvent
              ? [...progress.events, answerProgressEvent].slice(-24)
              : progress.events,
          },
        };
      })
    );
  }, []);

  const toggleProgress = useCallback((localMessageId) => {
    setMessages((current) =>
      current.map((item) => {
        if (item.id !== localMessageId || !item.progress) return item;
        return {
          ...item,
          progress: { ...item.progress, expanded: !item.progress.expanded },
        };
      })
    );
  }, []);

  const handleSubmit = async (event) => {
    event?.preventDefault();
    const content = draft.trim();
    if (!content || isStreaming || !companyId || !facilityId) return;

    const requestId = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    const localUserMessageId = `local-user-${requestId}`;
    const localAssistantMessageId = `local-assistant-${requestId}`;
    const controller = new AbortController();
    const requestScope = `${companyId}:${facilityId}`;
    let streamConversationId = selectedConversationId;

    streamControllerRef.current = controller;
    setDraft("");
    setIsStreaming(true);
    setMessages((current) => [
      ...current,
      {
        id: localUserMessageId,
        role: "user",
        content_markdown: content,
        status: "done",
        created_at: new Date().toISOString(),
      },
      {
        id: localAssistantMessageId,
        role: "assistant",
        content_markdown: "",
        status: "streaming",
        progress: createInitialProgress(),
        created_at: new Date().toISOString(),
      },
    ]);

    try {
      await streamFacilityAiChat({
        companyId,
        facilityId,
        conversationId: selectedConversationId,
        message: content,
        signal: controller.signal,
        onEvent: (eventPayload) => {
          if (scopeRef.current !== requestScope) return;

          if (eventPayload.event === "conversation_created") {
            streamConversationId = eventPayload.conversation_id;
            setSelectedConversationId(eventPayload.conversation_id);
            upsertConversation(eventPayload.conversation);
            return;
          }

          if (eventPayload.event === "answer_delta" || eventPayload.event === "delta") {
            appendAnswerDelta(localAssistantMessageId, eventPayload.delta);
            return;
          }

          appendProgressEvent(localAssistantMessageId, eventPayload);

          if (eventPayload.event === "answer_done") {
            return;
          }

          if (eventPayload.event === "done") {
            setMessages((current) =>
              current.map((item) => {
                if (item.id !== localAssistantMessageId) return item;
                return {
                  ...normalizeMessage(eventPayload.message),
                  id: localAssistantMessageId,
                  progress: {
                    ...(item.progress || createInitialProgress()),
                    status: "complete",
                    summary: "Processing complete",
                    expanded: false,
                  },
                };
              })
            );
            return;
          }

          if (eventPayload.event === "error") {
            updateStreamingMessage(localAssistantMessageId, {
              content_markdown: eventPayload.detail || eventPayload.message || "The AI response could not be completed.",
              status: "error",
            });
            updateProgressStatus(
              localAssistantMessageId,
              "error",
              "Request needs attention",
              true
            );
          }
        },
      });
    } catch (error) {
      const interrupted = error?.name === "AbortError";
      updateStreamingMessage(localAssistantMessageId, {
        content_markdown: interrupted
          ? "The response was stopped."
          : error?.message || "The AI response could not be completed.",
        status: "error",
      });
      updateProgressStatus(
        localAssistantMessageId,
        interrupted ? "stopped" : "error",
        interrupted ? "Generation stopped" : "Unable to complete request",
        true
      );
      if (!interrupted) {
        antdMessage.error(error?.message || "Unable to reach the AI service.");
      }
    } finally {
      if (streamControllerRef.current === controller) {
        streamControllerRef.current = null;
      }
      setIsStreaming(false);
      setMessages((current) =>
        current.map((item) => {
          if (item.id !== localAssistantMessageId || item.status !== "streaming") return item;
          return {
            ...item,
            progress: {
              ...(item.progress || createInitialProgress()),
              status: "complete",
              summary: "Processing complete",
              expanded: false,
            },
          };
        })
      );
      if (streamConversationId && scopeRef.current === requestScope) {
        loadConversations();
      }
    }
  };

  const handleStop = () => {
    streamControllerRef.current?.abort();
  };

  const renderConversationActions = (conversation) => (
    <Menu>
      <Menu.Item
        key="rename"
        icon={<EditOutlined />}
        onClick={() => {
          setRenameConversation(conversation);
          setRenameValue(conversation.title || "");
        }}
      >
        Rename
      </Menu.Item>
      <Menu.Item key="delete" icon={<DeleteOutlined />} danger onClick={() => handleDelete(conversation)}>
        Delete
      </Menu.Item>
    </Menu>
  );

  const renderProgressPanel = (item) => {
    const progress = item.progress;
    if (!progress) return null;

    const working = progress.status === "working";
    const failed = progress.status === "error" || progress.status === "stopped";
    const statusIcon = working ? (
      <LoadingOutlined spin />
    ) : failed ? (
      <ExclamationCircleOutlined />
    ) : (
      <CheckCircleOutlined />
    );

    return (
      <section
        className={`facility-ai-progress facility-ai-progress--${progress.status || "working"}`}
        aria-label="AI processing activity"
      >
        <button
          type="button"
          className="facility-ai-progress-toggle"
          onClick={() => toggleProgress(item.id)}
          aria-expanded={Boolean(progress.expanded)}
        >
          <span className="facility-ai-progress-status-icon" aria-hidden="true">{statusIcon}</span>
          <span className="facility-ai-progress-summary">
            <strong>{working ? "AI is working" : progress.status === "stopped" ? "Generation stopped" : failed ? "AI could not complete" : "AI completed"}</strong>
            <span>{progress.summary || "Processing request"}</span>
          </span>
          {progress.expanded ? <UpOutlined aria-hidden="true" /> : <DownOutlined aria-hidden="true" />}
        </button>
        {progress.expanded ? (
          <ol className="facility-ai-progress-events">
            {progress.events.map((progressEvent) => (
              <li
                className={`facility-ai-progress-event facility-ai-progress-event--${progressEvent.event}`}
                key={progressEvent.id}
              >
                <span className="facility-ai-progress-event-marker" aria-hidden="true" />
                <span>
                  <strong>{progressEvent.title}</strong>
                  <span>{progressEvent.detail}</span>
                </span>
              </li>
            ))}
          </ol>
        ) : null}
      </section>
    );
  };

  return (
    <div className="facility-ai-page">
      <aside className="facility-ai-sidebar" aria-label="AI conversations">
        <div className="facility-ai-sidebar-header">
          <div>
            <div className="facility-ai-sidebar-eyebrow">Facility</div>
            <h1>AI Assistant</h1>
          </div>
          <Tooltip title="New conversation">
            <Button
              type="text"
              icon={<PlusOutlined />}
              aria-label="New conversation"
              onClick={handleNewConversation}
              disabled={isStreaming}
            />
          </Tooltip>
        </div>

        <div className="facility-ai-conversation-list">
          {conversationsLoading ? (
            <div className="facility-ai-list-loading"><Spin size="small" /></div>
          ) : conversations.length === 0 ? (
            <div className="facility-ai-list-empty">No conversations yet.</div>
          ) : (
            conversations.map((conversation) => (
              <div
                className={`facility-ai-conversation-row${
                  conversation.id === selectedConversationId ? " is-active" : ""
                }`}
                key={conversation.id}
              >
                <button
                  type="button"
                  className="facility-ai-conversation-select"
                  onClick={() => handleSelectConversation(conversation.id)}
                  disabled={isStreaming}
                >
                  <span className="facility-ai-conversation-title">{conversation.title || "Untitled conversation"}</span>
                  <span className="facility-ai-conversation-date">
                    {formatConversationDate(conversation.last_message_at || conversation.updated_at)}
                  </span>
                </button>
                <Dropdown overlay={renderConversationActions(conversation)} trigger={["click"]} placement="bottomRight">
                  <button
                    type="button"
                    className="facility-ai-conversation-menu"
                    aria-label={`Actions for ${conversation.title || "conversation"}`}
                    disabled={isStreaming}
                  >
                    <MoreOutlined />
                  </button>
                </Dropdown>
              </div>
            ))
          )}
        </div>
      </aside>

      <main className="facility-ai-main">
        <header className="facility-ai-main-header">
          <div className="facility-ai-heading">
            <RobotOutlined aria-hidden="true" />
            <div>
              <h2>{selectedConversation?.title || "New conversation"}</h2>
              <p>Answers are scoped to this facility.</p>
            </div>
          </div>
        </header>

        <section className="facility-ai-messages" aria-live="polite">
          {messagesLoading ? (
            <div className="facility-ai-messages-loading"><Spin /></div>
          ) : messages.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="Ask a question about this facility"
              className="facility-ai-empty-state"
            />
          ) : (
            messages.map((item) => (
              <article className={`facility-ai-message facility-ai-message--${item.role}`} key={item.id}>
                <div className="facility-ai-message-role">{item.role === "user" ? "You" : "AI Assistant"}</div>
                {item.role === "assistant" ? (
                  <>
                    {renderProgressPanel(item)}
                    {item.content_markdown ? (
                      <div className="facility-ai-message-content">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{item.content_markdown}</ReactMarkdown>
                      </div>
                    ) : null}
                  </>
                ) : (
                  <div className="facility-ai-message-content">
                    <p>{item.content_markdown}</p>
                  </div>
                )}
              </article>
            ))
          )}
          <div ref={messagesEndRef} />
        </section>

        <form className="facility-ai-composer" onSubmit={handleSubmit}>
          <TextArea
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder="Ask about this facility"
            autoSize={{ minRows: 2, maxRows: 6 }}
            disabled={isStreaming}
            onPressEnter={(event) => {
              if (!event.shiftKey) {
                event.preventDefault();
                handleSubmit(event);
              }
            }}
          />
          <div className="facility-ai-composer-actions">
            {isStreaming ? (
              <Tooltip title="Stop generating">
                <Button icon={<StopOutlined />} onClick={handleStop} aria-label="Stop generating" />
              </Tooltip>
            ) : null}
            <Button
              type="primary"
              htmlType="submit"
              icon={<SendOutlined />}
              disabled={!draft.trim() || isStreaming}
            >
              Send
            </Button>
          </div>
        </form>
      </main>

      <Modal
        title="Rename conversation"
        visible={Boolean(renameConversation)}
        okText="Save"
        onOk={handleRename}
        confirmLoading={renameSaving}
        onCancel={() => setRenameConversation(null)}
      >
        <Input
          autoFocus
          value={renameValue}
          maxLength={255}
          onChange={(event) => setRenameValue(event.target.value)}
          onPressEnter={handleRename}
        />
      </Modal>
    </div>
  );
};

export default FacilityAiAssistant;
